import React, { useState } from 'react';
import { Maximize2, Minimize2, X, AlertCircle, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '../../utils/cn';

interface DebugLog {
  id: string;
  timestamp: Date;
  type: 'request' | 'response' | 'error';
  data: any;
}

interface DebugPanelProps {
  logs: DebugLog[];
  onClear: () => void;
  onClose: () => void;
}

export function DebugPanel({ logs, onClear, onClose }: DebugPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedLog, setSelectedLog] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'request' | 'response' | 'error'>('all');

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleLogSelection = (id: string) => {
    setSelectedLog(selectedLog === id ? null : id);
  };

  const filteredLogs = logs.filter(log => {
    if (filter === 'all') return true;
    return log.type === filter;
  });

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "fixed bottom-0 right-0 bg-gray-900 text-white rounded-tl-lg shadow-xl z-50 transition-all duration-300",
        isExpanded ? 'w-full md:w-2/3 h-2/3' : 'w-96 h-64'
      )}
    >
      <div className="flex items-center justify-between p-2 bg-gray-800 rounded-t-lg">
        <h3 className="font-semibold flex items-center gap-2">
          <span className="text-brand-400">OpenAI</span> Debug Panel
        </h3>
        <div className="flex items-center space-x-2">
          <div className="flex space-x-1 mr-2">
            <FilterButton 
              active={filter === 'all'} 
              onClick={() => setFilter('all')}
              label="All"
            />
            <FilterButton 
              active={filter === 'request'} 
              onClick={() => setFilter('request')}
              label="Requests"
              icon={<ArrowUpRight size={12} />}
              color="blue"
            />
            <FilterButton 
              active={filter === 'response'} 
              onClick={() => setFilter('response')}
              label="Responses"
              icon={<ArrowDownLeft size={12} />}
              color="green"
            />
            <FilterButton 
              active={filter === 'error'} 
              onClick={() => setFilter('error')}
              label="Errors"
              icon={<AlertCircle size={12} />}
              color="red"
            />
          </div>
          <button 
            onClick={onClear}
            className="text-gray-400 hover:text-white transition-colors"
          >
            Clear
          </button>
          <button 
            onClick={toggleExpand}
            className="text-gray-400 hover:text-white transition-colors"
          >
            {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      </div>
      
      <div className="overflow-y-auto h-[calc(100%-40px)] p-2">
        {filteredLogs.length === 0 ? (
          <div className="text-gray-500 text-center mt-4">
            No API calls recorded yet
          </div>
        ) : (
          <div className="space-y-2">
            {filteredLogs.map((log) => (
              <LogItem 
                key={log.id}
                log={log}
                isSelected={selectedLog === log.id}
                onToggle={() => toggleLogSelection(log.id)}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

interface FilterButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
  icon?: React.ReactNode;
  color?: 'blue' | 'green' | 'red';
}

function FilterButton({ active, onClick, label, icon, color }: FilterButtonProps) {
  return (
    <button 
      onClick={onClick}
      className={cn(
        "text-xs px-2 py-0.5 rounded flex items-center gap-1",
        active ? (
          color === 'blue' ? 'bg-blue-700' : 
          color === 'green' ? 'bg-green-700' : 
          color === 'red' ? 'bg-red-700' : 
          'bg-brand-600'
        ) : 'bg-gray-700 hover:bg-gray-600'
      )}
    >
      {icon}
      {label}
    </button>
  );
}

interface LogItemProps {
  log: DebugLog;
  isSelected: boolean;
  onToggle: () => void;
}

function LogItem({ log, isSelected, onToggle }: LogItemProps) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "p-2 rounded cursor-pointer transition-colors",
        log.type === 'request' 
          ? 'bg-blue-900 bg-opacity-30 border-l-4 border-blue-500' 
          : log.type === 'response'
          ? 'bg-green-900 bg-opacity-30 border-l-4 border-green-500'
          : 'bg-red-900 bg-opacity-30 border-l-4 border-red-500',
        isSelected ? 'ring-1 ring-gray-500' : 'hover:bg-opacity-40'
      )}
      onClick={onToggle}
    >
      <div className="flex justify-between items-center">
        <span className="font-medium flex items-center gap-1.5">
          {log.type === 'request' ? (
            <ArrowUpRight size={14} className="text-blue-400" />
          ) : log.type === 'response' ? (
            <ArrowDownLeft size={14} className="text-green-400" />
          ) : (
            <AlertCircle size={14} className="text-red-400" />
          )}
          
          {log.type === 'request' ? 'Request' : log.type === 'response' ? 'Response' : 'Error'}
          {log.data.type ? ` (${log.data.type})` : ''}
          {log.data.model ? ` - ${log.data.model}` : ''}
          {log.data.assistantId ? ` - Assistant: ${log.data.assistantId.substring(0, 8)}...` : ''}
        </span>
        <span className="text-xs text-gray-400">
          {log.timestamp.toLocaleTimeString()}
        </span>
      </div>
      
      {isSelected && (
        <motion.pre 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mt-2 p-2 bg-gray-800 rounded text-xs overflow-x-auto whitespace-pre-wrap"
        >
          {JSON.stringify(log.data, null, 2)}
        </motion.pre>
      )}
    </motion.div>
  );
}